import { useCallback, useContext, useMemo } from "react"
import { ErrorContext } from "@app/components/layout/error-snackbar"
import { TokenContext } from "@app/core/context/user/TokenContext"
import { SpringHttpError } from "@app/core/dto/spring-http-error"
import { getErrorCode } from "@app/core/error/error-handler"
import {
  addRequestParams,
  RequestParam,
  retry,
  isRequestHeaderTooLarge,
  REQUEST_HEADER_TOO_LARGE_ERROR_CODE,
} from "@app/core/services/http-service"

interface HttpHook {
  get(url: string, requestParams?: RequestParam[], signal?: AbortSignal): Promise<Response>
  post(url: string, payload: any, requestParams?: RequestParam[], signal?: AbortSignal): Promise<Response>
  put(url: string, payload: any, requestParams?: RequestParam[]): Promise<Response>
  postFile(url: string, payload: any, requestParams?: RequestParam[]): Promise<Response>
  putFile(url: string, payload: any, requestParams?: RequestParam[]): Promise<Response>
  deleteRequest(url: string, requestParams?: RequestParam[]): Promise<Response>
  deleteWithId(url: string, payload: any, requestParams?: RequestParam[]): Promise<Response>
  patch(url: string, payload: any, requestParams?: RequestParam[]): Promise<Response>
}

const SILENT_HTTP_ERROR_CODES = new Set([
  "USER_MUST_BE_SAME_SAME_ORGA",
  "BS_COMENV_RESULT_ALREADY_EXISTS",
  "FRAG_NOT_AVAILABLE",
  "SHARED_DOCUMENT_DELETED",
  "ORGANIZATION_MAX_ARCHIVED_PROJECT_COUNT_REACHED",
  "ORGANIZATION_MAX_UNARCHIVE_CREDIT_COUNT_REACHED",
])

export function isSilentHttpError(error: unknown): boolean {
  const code = getErrorCode(error)
  return code !== undefined && SILENT_HTTP_ERROR_CODES.has(code)
}

export function useHttp(): HttpHook {
  const openErrorSnackbar = useContext(ErrorContext)
  const { tokenRef, unsetCookieAndUserId, refreshToken } = useContext(TokenContext)

  const handleError = useCallback(
    async (r: Response): Promise<Response> => {
      try {
        if (r.status === 401) {
          console.error("Echec refresh token")
          await unsetCookieAndUserId()
        } else if (r.status === 404) {
          const clonedResponse = r.clone()
          const err: SpringHttpError = new SpringHttpError(await clonedResponse.json())
          if (err.message === "No message available" && err.error === "Not Found") {
            // The endpoint doesn't exist when trying
            console.info("Le endpoint backend n'existe pas: ", err.path)
            throw new Error("ENDPOINT_DOES_NOT_EXISTS")
          } else if (err.message === "No message available") {
            throw err
          } else {
            console.info("404 : no resource")
            // Nothing to do: 404 with error message means endpoint exists
            // but resource does not in db. So needs to be handle at component level
          }
        } else if (r.status >= 300) {
          const errorBody: string = await r.text()
          if (isRequestHeaderTooLarge(r.status, errorBody)) {
            // Rejected by nginx before reaching the API: the site's cookies (stale Cognito sessions that were never
            // cleaned up) no longer fit in its header buffers. Wipe them so that the next sign-in goes through.
            console.error("Request rejected by the reverse proxy: header or cookie too large")
            await unsetCookieAndUserId()
            throw new Error(REQUEST_HEADER_TOO_LARGE_ERROR_CODE)
          }
          let springHttpError
          try {
            springHttpError = new SpringHttpError(JSON.parse(errorBody))
          } catch {
            if (r.status === 403 || r.status === 401) {
              throw new Error(`${r.status}_FORBIDDEN`)
            } else {
              throw new Error(`Erreur ${r.status}`)
            }
          }
          throw new Error(springHttpError.message)
        }
        return r
      } catch (e: unknown) {
        // These codes are expected and handled by callers — don't show the snackbar.
        // USER_MUST_BE_SAME_SAME_ORGA → form-level error.
        // BS_COMENV_RESULT_ALREADY_EXISTS → the estimation context asks the queue for the update instead (US 2564).
        // FRAG_NOT_AVAILABLE → ensureFragForBimModel falls back to in-browser conversion.
        // SHARED_DOCUMENT_DELETED → the public dashboard renders a dedicated "document deleted" page.
        // ORGANIZATION_MAX_*_REACHED → the project status dialog shows the refusal inline (US 2461).
        if (e instanceof Error && !isSilentHttpError(e)) {
          openErrorSnackbar(e)
        }
        throw e
      }
    },
    [openErrorSnackbar, unsetCookieAndUserId]
  )

  const handleErrorRetry = useCallback(
    async (r: Response, input: RequestInfo, headers: Headers, options?: RequestInit): Promise<Response> => {
      if (r.status === 401) {
        return refreshToken()
          .then((newToken) => retry(input, newToken, headers, options))
          .then((response: Response) => handleError(response))
          .catch((reason) => reason)
      } else {
        return handleError(r)
      }
    },
    [handleError, refreshToken]
  )

  return useMemo<HttpHook>(
    () => ({
      async get(url: string, requestParams?: RequestParam[], signal?: AbortSignal): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current, requestParams)
        const options: RequestInit = {
          headers,
        }
        // Allow to interrupt the request, especially for file download
        if (signal) {
          options.signal = signal
        }

        const r = await fetch(finalUrl, options)
        return handleErrorRetry(r, finalUrl, headers, options)
      },
      async post(
        url: string,
        payload: any,
        requestParams?: RequestParam[],
        signal?: AbortSignal
      ): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current, requestParams)

        headers.set("Content-Type", "application/json")
        const options: RequestInit = {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        }
        if (signal) {
          options.signal = signal
        }

        const r = await fetch(finalUrl, options)
        return handleErrorRetry(r, finalUrl, headers, options)
      },
      async put(url: string, payload: any, requestParams?: RequestParam[]): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current, requestParams)

        headers.set("Content-Type", "application/json")
        const options: RequestInit = {
          method: "PUT",
          headers,
          body: JSON.stringify(payload),
        }
        const r = await fetch(finalUrl, options)
        return handleErrorRetry(r, finalUrl, headers, options)
      },
      async postFile(url: string, formData: FormData, requestParams?: RequestParam[]): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current, requestParams)
        const options: RequestInit = { method: "POST", body: formData, headers }
        const r = await fetch(finalUrl, options)
        return handleErrorRetry(r, finalUrl, headers, options)
      },
      putFile(url: string, formData: FormData): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current)
        const options: RequestInit = { method: "PUT", body: formData, headers }

        return fetch(finalUrl, options).then((r) => handleErrorRetry(r, finalUrl, headers, options))
      },
      // This function is not called "delete" because it is a reserved word in javascript
      async deleteRequest(url: string, requestParams?: RequestParam[]): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current, requestParams)
        const options: RequestInit = { method: "DELETE", headers }

        const r = await fetch(finalUrl, options)
        return handleErrorRetry(r, finalUrl, headers, options)
      },
      async deleteWithId(url: string, id: string | undefined): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current)
        const input: RequestInfo = `${finalUrl}/${id}`
        const options: RequestInit = { method: "DELETE", headers }

        const r = await fetch(input, options)
        return handleErrorRetry(r, input, headers, options)
      },
      async patch(url: string, payload: any, requestParams?: RequestParam[]): Promise<Response> {
        const { finalUrl, headers } = addRequestParams(url, tokenRef.current, requestParams)

        headers.set("Content-Type", "application/json")
        const options: RequestInit = {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        }
        const r = await fetch(finalUrl, options)
        return handleErrorRetry(r, finalUrl, headers, options)
      },
    }),
    [handleErrorRetry, tokenRef]
  )
}
